import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import { formatApiDate } from '../utils/date';

const estadoBadge = (estado) => {
  switch (estado) {
    case 'pendiente':
      return 'bg-amber-100 text-amber-800';
    case 'pagada':
      return 'bg-emerald-100 text-emerald-800';
    case 'procesando':
      return 'bg-blue-100 text-blue-800';
    case 'vencida':
      return 'bg-red-100 text-red-700';
    case 'plan_pago':
      return 'bg-indigo-100 text-indigo-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const estadoLabel = (estado) => {
  switch (estado) {
    case 'pendiente':
      return 'Pendiente';
    case 'pagada':
      return 'Pagada';
    case 'procesando':
      return 'Procesando';
    case 'vencida':
      return 'Vencida';
    case 'plan_pago':
      return 'Plan de pago';
    default:
      return estado || 'N/A';
  }
};

const formatearPeriodo = (mes, anio) => {
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return `${meses[(mes || 1) - 1]} ${anio}`;
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  return formatApiDate(fecha);
};

const formatearMonto = (monto) => parseFloat(monto || 0).toFixed(2);

const estadoFacturaBadge = (estado) => {
  switch (estado) {
    case 'emitida':
      return { label: 'Emitida', className: 'bg-emerald-100 text-emerald-800' };
    case 'error':
      return { label: 'Error', className: 'bg-red-100 text-red-700' };
    case 'pendiente':
      return { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' };
    default:
      return { label: 'Sin emitir', className: 'bg-gray-100 text-gray-700' };
  }
};

const BoletasClienteAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [boletas, setBoletas] = useState([]);
  const [selectedBoleta, setSelectedBoleta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState('pendientes');
  const [descargandoFacturaId, setDescargandoFacturaId] = useState(null);
  const [planConfig, setPlanConfig] = useState({ interesPorcentaje: 0, maxCuotas: 5 });
  const [planModal, setPlanModal] = useState({ isOpen: false, boleta: null });
  const [planCuotas, setPlanCuotas] = useState(2);
  const [planLoading, setPlanLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  useEffect(() => {
    cargarDatos();
    cargarConfigPlanPago();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const deduplicarPorPeriodo = (lista) => {
    const map = new Map();
    lista.forEach((boleta) => {
      const key = `${boleta.anio}-${boleta.mes}`;
      const previa = map.get(key);
      if (!previa) {
        map.set(key, boleta);
        return;
      }
      const fechaPrev = new Date(previa.fechaEmision || previa.createdAt || 0).getTime();
      const fechaNueva = new Date(boleta.fechaEmision || boleta.createdAt || 0).getTime();
      if (fechaNueva >= fechaPrev) {
        map.set(key, boleta);
      }
    });
    return Array.from(map.values());
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [clienteRes, boletasRes] = await Promise.all([
        axios.get(`/usuarios/${id}`),
        axios.get(`/boletas/usuario/${id}`).catch(() => ({ data: [] })),
      ]);

      setCliente(clienteRes.data);
      const boletasLimpias = deduplicarPorPeriodo(boletasRes.data || []);
      setBoletas(boletasLimpias);

      if (boletasLimpias.length > 0) {
        const seleccionId = selectedBoleta?.id;
        const boletaSeleccionada = seleccionId
          ? boletasLimpias.find((b) => b.id === seleccionId)
          : null;
        await seleccionarBoleta(boletaSeleccionada || boletasLimpias[0]);
      } else {
        setSelectedBoleta(null);
      }
    } catch (error) {
      console.error('Error al cargar boletas:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarConfigPlanPago = async () => {
    try {
      const response = await axios.get('/tarifario/plan-pago/config');
      setPlanConfig({
        interesPorcentaje: Number(response.data?.interesPorcentaje || 0),
        maxCuotas: Number(response.data?.maxCuotas || 5),
      });
    } catch (error) {
      console.error('No se pudo cargar la configuración del plan de pago:', error);
    }
  };

  const seleccionarBoleta = async (boletaBase) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`/boletas/${boletaBase.id}`);
      setSelectedBoleta(res.data);
    } catch (error) {
      console.error('No se pudo cargar el detalle de la boleta:', error);
      setSelectedBoleta(boletaBase);
    } finally {
      setDetailLoading(false);
    }
  };

  const cerrarModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  const obtenerNumeroFactura = (boleta) => {
    if (!boleta?.factura_punto_venta || !boleta?.factura_numero) return 'N/A';
    const pto = String(boleta.factura_punto_venta).padStart(4, '0');
    const nro = String(boleta.factura_numero).padStart(8, '0');
    return `${pto}-${nro}`;
  };

  const descargarFacturaPdf = async (boleta) => {
    if (!boleta?.id) return;
    setDescargandoFacturaId(boleta.id);
    try {
      const response = await axios.get(`/tarifario/pdf/factura/${boleta.id}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const padron = boleta?.usuario?.padron || 'cliente';
      link.href = url;
      link.setAttribute('download', `factura-${padron}-${boleta.mes}-${boleta.anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar factura:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo descargar la factura',
        message: error.response?.data?.message || 'La factura no está emitida o hubo un error.',
      });
    } finally {
      setDescargandoFacturaId(null);
    }
  };

  const calcularCuotasPlan = (montoTotal, cantidadCuotas) => {
    if (!cantidadCuotas || cantidadCuotas < 1) return [];
    const base = Number((montoTotal / cantidadCuotas).toFixed(2));
    const cuotas = Array.from({ length: cantidadCuotas }, () => base);
    const totalBase = Number((base * cantidadCuotas).toFixed(2));
    const ajuste = Number((montoTotal - totalBase).toFixed(2));
    cuotas[cuotas.length - 1] = Number((cuotas[cuotas.length - 1] + ajuste).toFixed(2));
    return cuotas;
  };

  const obtenerInicioPlanTexto = () => {
    const ahora = new Date();
    let mes = ahora.getMonth() + 2;
    let anio = ahora.getFullYear();
    if (mes > 12) {
      mes = 1;
      anio += 1;
    }
    return `${String(mes).padStart(2, '0')}/${anio}`;
  };

  const abrirPlanPago = (boleta) => {
    if (!boleta) return;
    const maxCuotas = Math.max(1, Number(planConfig.maxCuotas || 1));
    const cuotasInicial = Math.min(maxCuotas, maxCuotas >= 2 ? 2 : 1);
    setPlanCuotas(cuotasInicial);
    setPlanModal({ isOpen: true, boleta });
  };

  const cerrarPlanPago = () => {
    setPlanModal({ isOpen: false, boleta: null });
  };

  const confirmarPlanPago = async () => {
    if (!planModal.boleta) return;
    setPlanLoading(true);
    try {
      await axios.post('/tarifario/plan-pago', {
        boletaId: planModal.boleta.id,
        cantidadCuotas: Number(planCuotas),
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Plan de pago creado',
        message: 'El plan de pago se registró correctamente para esta boleta.',
      });

      setPlanModal({ isOpen: false, boleta: null });
      await cargarDatos();
    } catch (error) {
      console.error('Error al crear plan de pago:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'No se pudo crear el plan',
        message: error.response?.data?.message || 'Intente nuevamente en unos minutos.',
      });
    } finally {
      setPlanLoading(false);
    }
  };

  const boletasFiltradas = useMemo(() => {
    const lista = tab === 'pendientes'
      ? boletas.filter((b) => b.estado === 'pendiente')
      : boletas;

    return [...lista].sort((a, b) => {
      const fechaA = new Date(a.fechaVencimiento || a.createdAt || 0).getTime();
      const fechaB = new Date(b.fechaVencimiento || b.createdAt || 0).getTime();
      return fechaB - fechaA;
    });
  }, [boletas, tab]);

  const planBoletaTotal = Number(planModal.boleta?.total || 0);
  const planInteresPorcentaje = Number(planConfig.interesPorcentaje || 0);
  const planMontoInteres = Number(((planBoletaTotal * planInteresPorcentaje) / 100).toFixed(2));
  const planMontoTotal = Number((planBoletaTotal + planMontoInteres).toFixed(2));
  const cuotasPlan = calcularCuotasPlan(planMontoTotal, Number(planCuotas) || 0);
  const planMontoCuota = cuotasPlan[0] || 0;
  const planMontoCuotaUltima = cuotasPlan[cuotasPlan.length - 1] || 0;
  const planInicioTexto = obtenerInicioPlanTexto();
  const maxCuotasPlan = Math.max(1, Number(planConfig.maxCuotas || 1));

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-primary text-6xl animate-spin">refresh</span>
            <p className="text-[#617c89] mt-4">Cargando boletas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Sidebar />

      <main className="flex-1 overflow-y-auto bg-background-light dark:bg-background-dark">
        <div className="p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/administrativo/clientes/ver/${id}`)}
                className="flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Cliente</p>
                <h1 className="text-[#111618] dark:text-white text-3xl font-black leading-tight tracking-[-0.033em]">
                  {cliente?.nombre || 'Cliente'}
                </h1>
                <p className="text-sm text-[#617c89] dark:text-gray-400">Padrón {cliente?.padron}</p>
              </div>
            </div>
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Total boletas</p>
                <p className="text-2xl font-bold text-[#111618] dark:text-white">{boletas.length}</p>
              </div>
              <span className="material-symbols-outlined text-primary text-3xl">receipt_long</span>
            </div>
            <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Pendientes</p>
                <p className="text-2xl font-bold text-debt-red">
                  {boletas.filter((b) => b.estado === 'pendiente').length}
                </p>
              </div>
              <span className="material-symbols-outlined text-debt-red text-3xl">warning</span>
            </div>
            <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Pagadas</p>
                <p className="text-2xl font-bold text-active-green">
                  {boletas.filter((b) => b.estado === 'pagada').length}
                </p>
              </div>
              <span className="material-symbols-outlined text-active-green text-3xl">task_alt</span>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Tabla */}
            <div className="xl:col-span-2 bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl">
              <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
                <div>
                  <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Historial</p>
                  <h2 className="text-xl font-bold text-[#111618] dark:text-white">Boletas del cliente</h2>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTab('pendientes')}
                    className={`px-3 py-1 text-sm rounded-full border ${tab === 'pendientes' ? 'bg-primary text-white border-primary' : 'border-gray-300 dark:border-gray-700 text-[#617c89] dark:text-gray-300'}`}
                  >
                    Pendientes
                  </button>
                  <button
                    onClick={() => setTab('todas')}
                    className={`px-3 py-1 text-sm rounded-full border ${tab === 'todas' ? 'bg-primary text-white border-primary' : 'border-gray-300 dark:border-gray-700 text-[#617c89] dark:text-gray-300'}`}
                  >
                    Todas
                  </button>
                </div>
              </div>

              {boletasFiltradas.length === 0 ? (
                <div className="p-10 text-center text-[#617c89] dark:text-gray-400">
                  <span className="material-symbols-outlined text-5xl text-gray-400 block mb-3">draft</span>
                  No hay boletas {tab === 'pendientes' ? 'pendientes' : 'registradas'}.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs text-[#617c89] dark:text-gray-400 uppercase">
                      <tr>
                        <th className="px-6 py-3 text-left font-medium">Período</th>
                        <th className="px-6 py-3 text-left font-medium">Vencimiento</th>
                        <th className="px-6 py-3 text-left font-medium">Total</th>
                        <th className="px-6 py-3 text-left font-medium">Estado</th>
                        <th className="px-6 py-3 text-right font-medium">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {boletasFiltradas.map((boleta) => (
                        <tr
                          key={boleta.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm font-semibold text-[#111618] dark:text-white">
                            <div>
                              <p>{formatearPeriodo(boleta.mes, boleta.anio)}</p>
                              {boleta.usuarioPadreMedidor && (
                                <p className="text-xs font-medium text-amber-700 mt-1">
                                  Padrón padre {boleta.usuarioPadreMedidor.padron || boleta.usuarioPadreMedidor.id}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#617c89] dark:text-gray-300">
                            {formatearFecha(boleta.fechaVencimiento)}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#111618] dark:text-white font-semibold">
                            ${formatearMonto(boleta.total)}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${estadoBadge(boleta.estado)}`}>
                              {estadoLabel(boleta.estado)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => seleccionarBoleta(boleta)}
                              className="text-primary hover:text-primary/80 font-medium text-sm"
                            >
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Detalle */}
            <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Detalle</p>
                  <h3 className="text-lg font-bold text-[#111618] dark:text-white">
                    {selectedBoleta ? 'Boleta seleccionada' : 'Selecciona una boleta'}
                  </h3>
                </div>
                {detailLoading && (
                  <span className="material-symbols-outlined text-primary animate-spin">refresh</span>
                )}
              </div>

              {!selectedBoleta ? (
                <div className="text-center text-[#617c89] dark:text-gray-400 py-10">
                  <span className="material-symbols-outlined text-5xl text-gray-400 mb-2">description</span>
                  <p>Elige una boleta para ver el detalle.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Período</p>
                        <p className="text-xl font-bold text-[#111618] dark:text-white">
                          {formatearPeriodo(selectedBoleta.mes, selectedBoleta.anio)}
                        </p>
                        <p className="text-sm text-[#617c89] dark:text-gray-400">
                          Vence {formatearFecha(selectedBoleta.fechaVencimiento)}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${estadoBadge(selectedBoleta.estado)}`}>
                        {estadoLabel(selectedBoleta.estado)}
                      </span>
                    </div>
                  </div>

                  {selectedBoleta.usuarioPadreMedidor && (
                    <div className="border border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-900/20 rounded-lg p-4">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Consumo compartido</p>
                      <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                        Consumo proveniente del padrón padre {selectedBoleta.usuarioPadreMedidor.padron || selectedBoleta.usuarioPadreMedidor.id} - {selectedBoleta.usuarioPadreMedidor.nombre}
                      </p>
                    </div>
                  )}

                  <div className="space-y-3 border border-[#dbe2e6] dark:border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#617c89] dark:text-gray-400">Servicio base</span>
                      <span className="text-[#111618] dark:text-white font-semibold">
                        ${formatearMonto(selectedBoleta.monto_servicio_base)}
                      </span>
                    </div>
                    {parseFloat(selectedBoleta.monto_consumo || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#617c89] dark:text-gray-400">Consumo</span>
                        <span className="text-[#111618] dark:text-white font-semibold">
                          ${formatearMonto(selectedBoleta.monto_consumo)}
                        </span>
                      </div>
                    )}
                    {parseFloat(selectedBoleta.total_cargos_extras) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#617c89] dark:text-gray-400">Cargos extras</span>
                        <span className="text-[#111618] dark:text-white font-semibold">
                          ${formatearMonto(selectedBoleta.total_cargos_extras)}
                        </span>
                      </div>
                    )}
                    <div className="pt-3 border-t border-[#dbe2e6] dark:border-gray-700 flex justify-between items-center">
                      <span className="text-sm font-bold text-[#111618] dark:text-white">Total</span>
                      <span className="text-2xl font-bold text-primary">${formatearMonto(selectedBoleta.total)}</span>
                    </div>
                  </div>

                  {selectedBoleta.estado === 'plan_pago' && (
                    <div className="border border-indigo-200 bg-indigo-50 dark:border-indigo-900/30 dark:bg-indigo-900/20 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">Plan de pago activo</p>
                      <div className="grid grid-cols-2 gap-3 text-sm text-indigo-900 dark:text-indigo-100">
                        <p><span className="font-semibold">Cuotas:</span> {selectedBoleta.plan_pago_cantidad_cuotas || '-'}</p>
                        <p><span className="font-semibold">Interés:</span> {selectedBoleta.plan_pago_interes_porcentaje ?? 0}%</p>
                        <p><span className="font-semibold">Total plan:</span> ${formatearMonto(selectedBoleta.plan_pago_monto_total)}</p>
                        <p><span className="font-semibold">Cuota:</span> ${formatearMonto(selectedBoleta.plan_pago_monto_cuota)}</p>
                        <p><span className="font-semibold">Inicio:</span> {selectedBoleta.plan_pago_inicio_mes ? `${selectedBoleta.plan_pago_inicio_mes}/${selectedBoleta.plan_pago_inicio_anio}` : '-'}</p>
                      </div>
                    </div>
                  )}

                  {['pendiente', 'vencida'].includes(selectedBoleta.estado) && (
                    <button
                      onClick={() => abrirPlanPago(selectedBoleta)}
                      className="w-full h-10 rounded-lg border border-indigo-500 text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors"
                    >
                      Crear plan de pago
                    </button>
                  )}

                  {selectedBoleta.lectura && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-semibold text-[#111618] dark:text-white">Datos de consumo</p>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-[#617c89] dark:text-gray-400">Lectura anterior</p>
                          <p className="font-semibold text-[#111618] dark:text-white">{selectedBoleta.lectura.lecturaAnterior} m³</p>
                        </div>
                        <div>
                          <p className="text-[#617c89] dark:text-gray-400">Lectura actual</p>
                          <p className="font-semibold text-[#111618] dark:text-white">{selectedBoleta.lectura.lecturaActual} m³</p>
                        </div>
                        <div>
                          <p className="text-[#617c89] dark:text-gray-400">Consumo</p>
                          <p className="font-semibold text-[#111618] dark:text-white">{formatearMonto(selectedBoleta.consumo_m3)} m³</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border border-[#dbe2e6] dark:border-gray-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-[#111618] dark:text-white">
                        Facturación electrónica (ARCA)
                      </p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${estadoFacturaBadge(selectedBoleta.factura_estado).className}`}>
                        {estadoFacturaBadge(selectedBoleta.factura_estado).label}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[#617c89] dark:text-gray-400">Comprobante</p>
                        <p className="font-semibold text-[#111618] dark:text-white">{obtenerNumeroFactura(selectedBoleta)}</p>
                      </div>
                      <div>
                        <p className="text-[#617c89] dark:text-gray-400">CAE</p>
                        <p className="font-semibold text-[#111618] dark:text-white">{selectedBoleta.factura_cae || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[#617c89] dark:text-gray-400">Fecha emisión</p>
                        <p className="font-semibold text-[#111618] dark:text-white">
                          {selectedBoleta.factura_fecha_emision ? formatearFecha(selectedBoleta.factura_fecha_emision) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#617c89] dark:text-gray-400">Vto. CAE</p>
                        <p className="font-semibold text-[#111618] dark:text-white">
                          {selectedBoleta.factura_cae_vto ? formatearFecha(selectedBoleta.factura_cae_vto) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    {selectedBoleta.factura_estado === 'error' && selectedBoleta.factura_error && (
                      <div className="bg-red-50 text-red-700 text-xs px-3 py-2 rounded">
                        {selectedBoleta.factura_error}
                      </div>
                    )}
                    <button
                      onClick={() => descargarFacturaPdf(selectedBoleta)}
                      disabled={descargandoFacturaId === selectedBoleta.id || selectedBoleta.factura_estado !== 'emitida'}
                      className="w-full h-10 rounded-lg border border-emerald-500 text-emerald-700 font-semibold hover:bg-emerald-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {descargandoFacturaId === selectedBoleta.id
                        ? 'Descargando factura...'
                        : selectedBoleta.factura_estado === 'emitida'
                          ? 'Descargar factura (PDF)'
                          : 'Factura pendiente'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={modal.isOpen}
        onClose={cerrarModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
      />

      {planModal.isOpen && planModal.boleta && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-background-dark rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#111618] dark:text-white">Crear plan de pago</h3>
              <button
                onClick={cerrarPlanPago}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4 text-sm text-[#111618] dark:text-gray-200">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Boleta</p>
                <p className="font-semibold">{formatearPeriodo(planModal.boleta.mes, planModal.boleta.anio)}</p>
                <p className="text-gray-600 dark:text-gray-400">
                  Monto actual: ${formatearMonto(planBoletaTotal)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Interés</p>
                  <p className="font-semibold">{planInteresPorcentaje}%</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    +${formatearMonto(planMontoInteres)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Total plan</p>
                  <p className="font-semibold">${formatearMonto(planMontoTotal)}</p>
                  <p className="text-gray-600 dark:text-gray-400">Inicio: {planInicioTexto}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cantidad de cuotas (máx. {maxCuotasPlan})
                </label>
                <select
                  value={planCuotas}
                  onChange={(e) => setPlanCuotas(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-[#0f1a21] dark:border-gray-700"
                >
                  {Array.from({ length: maxCuotasPlan }, (_, i) => i + 1).map((cuota) => (
                    <option key={cuota} value={cuota}>
                      {cuota} {cuota === 1 ? 'cuota' : 'cuotas'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900/30 dark:bg-indigo-900/20 p-4">
                <p className="text-sm text-indigo-900 dark:text-indigo-100">
                  Cuota estimada: ${formatearMonto(planMontoCuota)}
                  {planMontoCuotaUltima !== planMontoCuota
                    ? ` (última ${formatearMonto(planMontoCuotaUltima)})`
                    : ''}
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={cerrarPlanPago}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={planLoading}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarPlanPago}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={planLoading}
              >
                {planLoading ? 'Creando...' : 'Confirmar plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoletasClienteAdmin;
